"use client";

import {
	Button,
	type ButtonProps,
	Carousel,
	Container,
	DealCard,
	type DealCardProps,
	type DefaultImageProps,
	FullScreenButton,
	Heading,
	Icon,
	Image,
	type ImageProps,
	InputNumber,
	Link,
	Modal,
	Price,
	type PriceProps,
	Rating,
	type RatingProps,
	Tag,
	Text
} from "@pamperedchef/design-system";
import { className } from "@/app/utilities/className";
import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type CSSProperties,
	type Dispatch,
	type PointerEvent as ReactPointerEvent,
	type SetStateAction
} from "react";

import type { AddItemsToHybrisCartRequest } from "@/infrastructure/hybris";

import { AddtoCart } from "../AddToCart";
import * as styles from "./PDPBannerArea.module.scss";

function aspectRatio16By9<
	ImageComponentProps = DefaultImageProps
>(): NonNullable<ImageProps<ImageComponentProps>["aspectRatio"]> {
	return "16:9" as NonNullable<
		ImageProps<ImageComponentProps>["aspectRatio"]
	>;
}

/**
 * Widen hosted player pages use `/view/video/...` and should load in an iframe.
 * Direct asset URLs under `/content/...` (e.g. `.mp4`) use the HTML5 video element.
 */
function isWidenViewPlayerUrl(url: string): boolean {
	try {
		const u = new URL(url);
		return (
			u.hostname.includes("widen.net") &&
			u.pathname.includes("/view/video/")
		);
	} catch {
		return false;
	}
}

function resolvePosterSrc(src: unknown): string | undefined {
	if (!src) return undefined;
	if (typeof src === "string") return src;
	if (
		typeof src === "object" &&
		"src" in src &&
		typeof (src as { src?: unknown }).src === "string"
	) {
		return (src as { src: string }).src;
	}
	return undefined;
}

type GalleryImageItem<ImageComponentProps = DefaultImageProps> = {
	type: "image";
	media: ImageProps<ImageComponentProps>;
};

type GalleryVideoItem<ImageComponentProps = DefaultImageProps> = {
	type: "video";
	videoSrc: string;
	poster: ImageProps<ImageComponentProps>;
};

export type PDPGalleryItem<ImageComponentProps = DefaultImageProps> =
	| GalleryImageItem<ImageComponentProps>
	| GalleryVideoItem<ImageComponentProps>;

export interface PDPBannerAreaProps<ImageComponentProps = DefaultImageProps> {
	product: {
		info: {
			country: AddItemsToHybrisCartRequest["country"];
			purchaseType: AddItemsToHybrisCartRequest["purchaseType"];
			name: string;
			itemNumber: string;
			rating: RatingProps;
			inventory?: number;
			discontinued?: boolean;
			inventoryLabels?: {
				outOfStock: {
					title: string;
					description: string;
				};
				lowStock: string;
				discontinued: {
					title: string;
					description: string;
				};
			};
			bestSeller?: boolean;
			finalSale?: boolean;
			newThisMonth?: boolean;
			availableWhileSuppliesLast?: boolean;
			urgentLabels?: {
				bestSeller: string;
				finalSale: string;
				newThisMonth: string;
				availableWhileSuppliesLast: string;
			};
			price: PriceProps;
			deals: DealCardProps[];
		};
		cta: ButtonProps;
	};
	gallery: PDPGalleryItem<ImageComponentProps>[];
	dealLabels: {
		singular: string;
		plural: string;
	};
	wishlistLabel?: string;
	registryLabel?: string;
	errorMessage: string | null;
	alreadyAddedMessage: string;
}

interface GalleryModalProps<ImageComponentProps> {
	media: PDPGalleryItem<ImageComponentProps>[];
	activeIndex: number;
	productName: string;
	isOpen: boolean;
	setOpen: Dispatch<SetStateAction<boolean>>;
}

interface GalleryThumbnailButtonProps<ImageComponentProps = DefaultImageProps> {
	item: PDPGalleryItem<ImageComponentProps>;
	index: number;
	isSelected: boolean;
	onClick: () => void;
	buttonClassName: string;
	activeClassName: string;
}

function GalleryThumbnailButton<ImageComponentProps = DefaultImageProps>({
	item,
	index,
	isSelected,
	onClick,
	buttonClassName,
	activeClassName
}: GalleryThumbnailButtonProps<ImageComponentProps>) {
	return (
		<button
			type="button"
			className={`${buttonClassName}${isSelected ? ` ${activeClassName}` : ""}`}
			onClick={onClick}
			aria-label={`View ${item.type === "video" ? "video" : "image"} ${index + 1}`}
		>
			{item.type === "video" ? (
				<>
					<Image<ImageComponentProps>
						{...item.poster}
						aspectRatio="1:1"
						alt={item.poster.alt || ""}
					/>
					<span
						className={styles.videoThumbnailIndicator}
						aria-hidden="true"
					>
						<Icon name="Play" size={16} />
					</span>
				</>
			) : (
				<Image<ImageComponentProps>
					{...item.media}
					aspectRatio="1:1"
					alt={item.media.alt || ""}
				/>
			)}
		</button>
	);
}

function ModalVideoPlayer({
	videoSrc,
	posterSrc
}: {
	videoSrc: string;
	posterSrc: string | undefined;
}) {
	return isWidenViewPlayerUrl(videoSrc) ? (
		<iframe
			title="Product video"
			src={videoSrc}
			className={styles.videoIframe}
			allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
			allowFullScreen
		/>
	) : (
		<video
			controls
			playsInline
			preload="metadata"
			poster={posterSrc}
			className={styles.videoPlayer}
		>
			<source src={videoSrc} />
		</video>
	);
}

function clampToRange(idx: number, length: number) {
	return Math.max(0, Math.min(idx, length - 1));
}

function touchDistance(a: Touch, b: Touch) {
	return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

interface PinchZoomImageSlideProps<ImageComponentProps> {
	image: ImageProps<ImageComponentProps>;
}

// Pinch + double-tap + pan-while-zoomed for the mobile gallery carousel.
// React synthetic touch events are passive, so preventDefault() is silently
// dropped — we attach native listeners with { passive: false } here.
function PinchZoomImageSlide<ImageComponentProps = DefaultImageProps>({
	image
}: PinchZoomImageSlideProps<ImageComponentProps>) {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [scale, setScale] = useState(1);
	const [offset, setOffset] = useState<{ x: number; y: number }>({
		x: 0,
		y: 0
	});
	const scaleRef = useRef(1);
	const offsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
	const lastTapRef = useRef<number>(0);
	const pinchStartRef = useRef<{
		distance: number;
		scale: number;
		offset: { x: number; y: number };
	} | null>(null);
	const panStartRef = useRef<{
		x: number;
		y: number;
		offset: { x: number; y: number };
	} | null>(null);

	useEffect(() => {
		const el = wrapperRef.current;
		if (!el) return;

		const setScaleBoth = (next: number) => {
			scaleRef.current = next;
			setScale(next);
		};
		const setOffsetBoth = (next: { x: number; y: number }) => {
			offsetRef.current = next;
			setOffset(next);
		};

		const handleTouchStart = (e: TouchEvent) => {
			if (e.touches.length === 2) {
				e.preventDefault();
				e.stopPropagation();
				pinchStartRef.current = {
					distance: touchDistance(e.touches[0]!, e.touches[1]!),
					scale: scaleRef.current,
					offset: offsetRef.current
				};
			} else if (e.touches.length === 1) {
				const now = Date.now();
				if (now - lastTapRef.current < 300) {
					// Double-tap → toggle zoom (and stop the carousel from
					// treating the second tap as a drag start).
					e.preventDefault();
					e.stopPropagation();
					if (scaleRef.current > 1) {
						setScaleBoth(1);
						setOffsetBoth({ x: 0, y: 0 });
					} else {
						setScaleBoth(2);
					}
				}
				lastTapRef.current = now;
				if (scaleRef.current > 1) {
					e.stopPropagation();
					panStartRef.current = {
						x: e.touches[0]!.clientX,
						y: e.touches[0]!.clientY,
						offset: offsetRef.current
					};
				}
			}
		};

		const handleTouchMove = (e: TouchEvent) => {
			if (e.touches.length === 2 && pinchStartRef.current) {
				e.preventDefault();
				e.stopPropagation();
				const d = touchDistance(e.touches[0]!, e.touches[1]!);
				const factor = d / pinchStartRef.current.distance;
				const next = Math.max(
					1,
					Math.min(4, pinchStartRef.current.scale * factor)
				);
				setScaleBoth(next);
				if (next === 1) setOffsetBoth({ x: 0, y: 0 });
			} else if (
				e.touches.length === 1 &&
				panStartRef.current &&
				scaleRef.current > 1
			) {
				e.preventDefault();
				e.stopPropagation();
				const dx = e.touches[0]!.clientX - panStartRef.current.x;
				const dy = e.touches[0]!.clientY - panStartRef.current.y;
				setOffsetBoth({
					x: panStartRef.current.offset.x + dx,
					y: panStartRef.current.offset.y + dy
				});
			}
		};

		const handleTouchEnd = () => {
			pinchStartRef.current = null;
			panStartRef.current = null;
		};

		el.addEventListener("touchstart", handleTouchStart, { passive: false });
		el.addEventListener("touchmove", handleTouchMove, { passive: false });
		el.addEventListener("touchend", handleTouchEnd);
		el.addEventListener("touchcancel", handleTouchEnd);

		return () => {
			el.removeEventListener("touchstart", handleTouchStart);
			el.removeEventListener("touchmove", handleTouchMove);
			el.removeEventListener("touchend", handleTouchEnd);
			el.removeEventListener("touchcancel", handleTouchEnd);
		};
	}, []);

	const contentStyle = useMemo<CSSProperties>(
		() => ({
			transform:
				scale === 1
					? "none"
					: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
			transition:
				scale === 1
					? "transform 200ms ease-out"
					: "transform 80ms linear",
			willChange: "transform"
		}),
		[scale, offset]
	);

	// touch-action: pan-x while at scale 1 lets single-finger horizontal
	// swipes pass to the DS Carousel for slide nav, while disabling the
	// browser's native pinch/double-tap zoom so our handler can take over.
	// Once zoomed, switch to none so single-finger pans go to us instead.
	return (
		<div
			ref={wrapperRef}
			className={styles.pinchZoomSlide}
			style={{
				touchAction: scale > 1 ? "none" : "pan-x"
			}}
		>
			<div className={styles.pinchZoomContent} style={contentStyle}>
				<Image<ImageComponentProps>
					{...image}
					alt={image.alt || ""}
					className={styles.galleryModalImage}
				/>
			</div>
		</div>
	);
}

function GalleryModal<ImageComponentProps = DefaultImageProps>({
	media,
	activeIndex,
	productName,
	isOpen,
	setOpen
}: GalleryModalProps<ImageComponentProps>): JSX.Element {
	const [selectedIndex, setSelectedIndex] = useState(
		clampToRange(activeIndex, media.length)
	);
	const [hovering, setHovering] = useState(false);
	const [hoverPos, setHoverPos] = useState<{ x: number; y: number }>({
		x: 50,
		y: 50
	});

	useEffect(() => {
		if (isOpen) setSelectedIndex(clampToRange(activeIndex, media.length));
	}, [activeIndex, media.length, isOpen]);

	useEffect(() => {
		setHovering(false);
	}, [selectedIndex]);

	const onPointerMove = useCallback(
		(e: ReactPointerEvent<HTMLDivElement>) => {
			if (e.pointerType !== "mouse") return;
			const r = e.currentTarget.getBoundingClientRect();
			setHoverPos({
				x: ((e.clientX - r.left) / r.width) * 100,
				y: ((e.clientY - r.top) / r.height) * 100
			});
		},
		[]
	);

	const onPointerEnter = useCallback(
		(e: ReactPointerEvent<HTMLDivElement>) => {
			if (e.pointerType === "mouse") setHovering(true);
		},
		[]
	);

	const onPointerLeave = useCallback(() => setHovering(false), []);

	const selectedMedia = media[selectedIndex];

	const mainImageStyle = useMemo<CSSProperties>(() => {
		if (!hovering || selectedMedia?.type !== "image") {
			return {
				transform: "none",
				transition: "transform 200ms ease-out"
			};
		}
		return {
			transform: "scale(1.5)",
			transformOrigin: `${hoverPos.x}% ${hoverPos.y}%`,
			transition: "transform 120ms ease-out"
		};
	}, [hovering, hoverPos, selectedMedia?.type]);

	if (!selectedMedia) return <></>;

	const carouselSlides = media.map((item, index) => {
		if (item.type === "video") {
			return (
				<div key={index} className={styles.carouselMediaWrapper}>
					<ModalVideoPlayer
						videoSrc={item.videoSrc}
						posterSrc={resolvePosterSrc(item.poster.src)}
					/>
					<Text
						styledAs="xsr"
						className={styles.galleryModalZoomHint}
					>
						<Icon name="ZoomIn" size={12} />
						No zoom available for videos
					</Text>
				</div>
			);
		}
		return (
			<div key={index} className={styles.carouselMediaWrapper}>
				<PinchZoomImageSlide<ImageComponentProps> image={item.media} />
				<Text styledAs="xsr" className={styles.galleryModalZoomHint}>
					<Icon name="ZoomIn" size={12} />
					Pinch or tap to zoom
				</Text>
			</div>
		);
	});

	return (
		<Modal
			className={styles.galleryModal}
			size="xl"
			title={productName}
			isOpen={isOpen}
			setOpen={setOpen}
		>
			{/* Desktop layout — CSS-hidden at < lg, no JS layout switch */}
			<div className={styles.galleryModalGrid}>
				<div className={styles.galleryModalThumbnails}>
					{media.map((item, index) => (
						<GalleryThumbnailButton<ImageComponentProps>
							key={index}
							item={item}
							index={index}
							isSelected={selectedIndex === index}
							onClick={() => setSelectedIndex(index)}
							buttonClassName={styles.galleryModalThumbnail}
							activeClassName={styles.galleryModalThumbnailActive}
						/>
					))}
				</div>
				<div
					className={styles.galleryModalMainImage}
					onPointerMove={onPointerMove}
					onPointerEnter={onPointerEnter}
					onPointerLeave={onPointerLeave}
				>
					{selectedMedia.type === "video" ? (
						<ModalVideoPlayer
							videoSrc={selectedMedia.videoSrc}
							posterSrc={resolvePosterSrc(
								selectedMedia.poster.src
							)}
						/>
					) : (
						<div
							className={styles.imageZoomWrapper}
							style={mainImageStyle}
						>
							<Image<ImageComponentProps>
								{...selectedMedia.media}
								alt={selectedMedia.media.alt || ""}
								className={styles.galleryModalImage}
							/>
						</div>
					)}
				</div>
				<Text styledAs="xsr" className={styles.galleryModalZoomHint}>
					<Icon name="ZoomIn" size={12} />
					{selectedMedia.type === "image"
						? "Hover over image to zoom"
						: "No zoom available for videos"}
				</Text>
			</div>

			{/* Mobile layout — CSS-hidden at lg+, no JS layout switch */}
			<div className={styles.galleryCarouselContainer}>
				<Carousel
					controlledIndex={selectedIndex}
					setControlledIndex={setSelectedIndex}
					useDrag
					useKeyboard
					onIndexChange={setSelectedIndex}
					className={styles.galleryCarousel}
					classNames={{ slide: styles.carouselSlide }}
					slides={carouselSlides}
				/>
				{media.length > 1 && (
					<div className={styles.galleryCarouselThumbnails}>
						{media.map((item, index) => (
							<GalleryThumbnailButton<ImageComponentProps>
								key={index}
								item={item}
								index={index}
								isSelected={selectedIndex === index}
								onClick={() => setSelectedIndex(index)}
								buttonClassName={
									styles.galleryCarouselThumbnail
								}
								activeClassName={
									styles.galleryModalThumbnailActive
								}
							/>
						))}
					</div>
				)}
			</div>
		</Modal>
	);
}

export function PDPBannerArea<ImageComponentProps = DefaultImageProps>(
	props: PDPBannerAreaProps<ImageComponentProps>
): JSX.Element {
	const media = props.gallery.slice(0, 5);
	const [numberOfItems, setNumberOfItems] = useState(1);
	const [showAllDeals, setShowAllDeals] = useState(
		props.product.info.deals.length <= 2
	);
	const [activeImage, setActiveImage] = useState(0);
	const [galleryOpen, setGalleryOpen] = useState(false);
	const [galleryIndex, setGalleryIndex] = useState(0);
	const activeIndex = Math.min(activeImage, Math.max(0, media.length - 1));
	const activeItem = media[activeIndex];

	function openGallery(index: number) {
		setGalleryIndex(index);
		setGalleryOpen(true);
	}

	return (
		<Container>
			<div className={styles.container}>
				{media.length > 0 && (
					<div className={styles.galleryContainer}>
						<div className={styles.mobileGallery}>
							{activeItem && (
								<div className={styles.gallery}>
									<div className={styles.mainStage}>
										<div
											className={`${styles.imageContainer} ${styles.heroMedia} ${activeItem.type === "video" ? styles.videoMedia : ""}`}
										>
											{activeItem.type === "video" ? (
												<>
													<Image<ImageComponentProps>
														{...activeItem.poster}
														aspectRatio={aspectRatio16By9<ImageComponentProps>()}
														alt={
															activeItem.poster
																.alt || ""
														}
													/>
													<button
														type="button"
														className={
															styles.videoTrigger
														}
														aria-label="Play product video"
														onClick={() =>
															openGallery(
																activeIndex
															)
														}
													>
														<span
															className={
																styles.videoPlayIcon
															}
															aria-hidden="true"
														>
															<Icon
																name="Play"
																size={24}
															/>
														</span>
													</button>
												</>
											) : (
												<Image<ImageComponentProps>
													{...activeItem.media}
													alt={
														activeItem.media.alt ||
														""
													}
												/>
											)}
											<FullScreenButton
												state="collapsed"
												className={styles.expandButton}
												onClick={() =>
													openGallery(activeIndex)
												}
											/>
										</div>
									</div>
									{media.length > 1 && (
										<div
											className={styles.thumbnailStrip}
											aria-label="Product media thumbnails"
										>
											{media.map((item, index) => (
												<button
													key={index}
													type="button"
													aria-pressed={
														activeIndex === index
													}
													aria-label={`Show media ${index + 1} of ${media.length}`}
													{...className(
														styles.thumbnailButton,
														item.type === "video"
															? styles.videoMedia
															: "",
														activeIndex === index
															? styles.thumbnailSelected
															: ""
													)}
													onClick={() =>
														setActiveImage(index)
													}
												>
													<div
														className={`${styles.thumbnailInner} ${styles.thumbnailMedia}`}
													>
														{item.type ===
														"video" ? (
															<>
																<Image<ImageComponentProps>
																	{...item.poster}
																	aspectRatio={aspectRatio16By9<ImageComponentProps>()}
																	alt={
																		item
																			.poster
																			.alt ||
																		""
																	}
																/>
																<span
																	className={
																		styles.thumbnailPlayIcon
																	}
																	aria-hidden
																>
																	<Icon
																		name="Play"
																		size={
																			20
																		}
																	/>
																</span>
															</>
														) : (
															<Image<ImageComponentProps>
																{...item.media}
																alt={
																	item.media
																		.alt ||
																	""
																}
															/>
														)}
													</div>
												</button>
											))}
										</div>
									)}
								</div>
							)}
						</div>

						{/* Desktop: all images in a grid, first image larger */}
						<div className={styles.desktopGallery}>
							<div className={styles.desktopGalleryGrid}>
								{media.map((item, index) => (
									<div
										key={index}
										className={`${styles.desktopGalleryItem} ${index === 0 || item.type === "video" ? styles.desktopGalleryItemFullWidth : ""}`}
									>
										{item.type === "video" ? (
											<Image<ImageComponentProps>
												{...item.poster}
												aspectRatio="1:1"
												alt={item.poster.alt || ""}
											/>
										) : (
											<Image<ImageComponentProps>
												{...item.media}
												aspectRatio="1:1"
												alt={item.media.alt || ""}
											/>
										)}
										<button
											type="button"
											className={
												styles.desktopGalleryClickOverlay
											}
											onClick={() => openGallery(index)}
											aria-label={`Open ${item.type === "video" ? "video" : "image"} ${index + 1} in gallery`}
										/>
										{item.type === "video" && (
											<span
												className={
													styles.desktopGalleryPlayIcon
												}
												aria-hidden="true"
											>
												<Icon name="Play" size={32} />
											</span>
										)}
										{item.type !== "video" && (
											<FullScreenButton
												state="collapsed"
												className={
													styles.desktopGalleryExpandBtn
												}
												tabIndex={-1}
												aria-hidden="true"
											/>
										)}
									</div>
								))}
							</div>
						</div>

						<GalleryModal<ImageComponentProps>
							media={media}
							activeIndex={galleryIndex}
							productName={props.product.info.name}
							isOpen={galleryOpen}
							setOpen={setGalleryOpen}
						/>
					</div>
				)}
				<div>
					<Heading
						tag="h1"
						styledAs="h1"
						className={`mb-1 ${styles.productTitle}`}
					>
						{props.product.info.name}
					</Heading>
					<Text styledAs="mdr" className="mb-1">
						Item Number: #{props.product.info.itemNumber}
					</Text>
					{props.product.info.discontinued ? (
						<>
							<Text tag="h2" styledAs="smr" className="mb-1">
								{
									props.product.info?.inventoryLabels
										?.discontinued?.title
								}
							</Text>
							<Text styledAs="xsr" className="mb-0">
								{
									props.product.info?.inventoryLabels
										?.discontinued?.description
								}
							</Text>
						</>
					) : (
						<>
							<Rating
								{...props.product.info.rating}
								className="mb-1"
							/>
							<Price
								{...props.product.info.price}
								className={`mb-2 ${styles.price}`}
							/>

							{(props.product.info.inventory || 0) > 0 &&
								(props.product.info.inventory || 0) < 400 && (
									<Text styledAs="smr" className="mb-1">
										{
											props.product.info?.inventoryLabels
												?.lowStock
										}
									</Text>
								)}

							<div className={styles.urgentLabels}>
								{props.product.info?.bestSeller && (
									<Tag
										color="success"
										kind="subtle"
										size="sm"
										icon={{ name: "star", size: "sm" }}
										label={
											props.product.info?.urgentLabels
												?.bestSeller || ""
										}
									/>
								)}
								{props.product.info?.finalSale && (
									<Tag
										color="red"
										kind="subtle"
										size="sm"
										label={
											props.product.info?.urgentLabels
												?.finalSale || ""
										}
									/>
								)}
								{props.product.info?.newThisMonth && (
									<Tag
										color="teal"
										kind="subtle"
										size="sm"
										label={
											props.product.info?.urgentLabels
												?.newThisMonth || ""
										}
									/>
								)}
								{props.product.info
									?.availableWhileSuppliesLast && (
									<Tag
										color="yellow"
										kind="subtle"
										size="sm"
										label={
											props.product.info?.urgentLabels
												?.availableWhileSuppliesLast ||
											""
										}
									/>
								)}
							</div>

							<div className={styles.deals}>
								{props.product.info.deals
									.slice(
										0,
										showAllDeals
											? props.product.info.deals.length
											: 2
									)
									.map((deal, index) => (
										<DealCard
											key={index}
											{...deal}
											className={index ? "mt-1" : ""}
										/>
									))}
								{!showAllDeals && (
									<Button
										kind="tertiary"
										size="lg"
										onClick={() => setShowAllDeals(true)}
										className={styles.showAllDeals}
									>{`+${props.product.info.deals.length - 2} ${props.product.info.deals.length > 3 ? props.dealLabels.plural : props.dealLabels.singular}`}</Button>
								)}
							</div>

							{(props.product.info?.inventory || 1) > 0 ? (
								<div className={styles.ctaSection}>
									<InputNumber
										value={numberOfItems}
										onChange={setNumberOfItems}
										showControls
									/>
									<AddtoCart
										payload={{
											quantity: `${numberOfItems}`,
											productCode:
												props.product.info.itemNumber,
											purchaseType:
												props.product.info.purchaseType
										}}
										buttonProps={{
											kind: "primary",
											size: "lg",
											...props.product.cta,
											className: styles.cta
										}}
										alreadyAddedMessage={
											props.alreadyAddedMessage
										}
										errorMessage={props.errorMessage}
									/>
									{(props.wishlistLabel ||
										props.registryLabel) && (
										<div
											className={styles.secondaryActions}
										>
											{props.wishlistLabel && (
												<Link
													kind="tertiary"
													size="sm"
													href="/wishlist"
												>
													<Icon
														name="heart"
														size="sm"
													/>
													{props.wishlistLabel}
												</Link>
											)}
											{props.registryLabel && (
												<Link
													kind="tertiary"
													size="sm"
													href="/registry"
												>
													<Icon
														name="gift"
														size="sm"
													/>
													{props.registryLabel}
												</Link>
											)}
										</div>
									)}
								</div>
							) : (
								<>
									<Text styledAs="smr" className="mb-1">
										{
											props.product.info?.inventoryLabels
												?.outOfStock.title
										}
									</Text>
									<Text styledAs="xsr" className="mb-0">
										{
											props.product.info?.inventoryLabels
												?.outOfStock.description
										}
									</Text>
								</>
							)}
						</>
					)}
				</div>
			</div>
		</Container>
	);
}
